import React, { useEffect } from 'react';
import { captureException } from '@sentry/react';
import { collection, onSnapshot, query, where } from '@firebase/firestore';

import type {
  CustomStep,
  CustomStepType,
  HealthCategoryId,
  IntakeStatusCustomSteps,
} from '@shared/patient-intake-status';

import { db } from '../../../../firebase-config.js';

type GetQueryParams = {
  patientId: string;
  healthCategoryIds?: string[];
};

const getQuery = ({ patientId, healthCategoryIds }: GetQueryParams) => {
  if (healthCategoryIds) {
    return query(
      collection(db, 'patients', patientId, 'intakeStatus'),
      where('stepType', '==', 'CUSTOM_STEP'),
      where('isDeleted', '!=', true),
      where('healthCategoryId', 'in', healthCategoryIds),
    );
  }

  return query(
    collection(db, 'patients', patientId, 'intakeStatus'),
    where('stepType', '==', 'CUSTOM_STEP'),
    where('isDeleted', '!=', true),
  );
};

type UseGetIntakeStatusCustomStepsParams = {
  patientId: string;
  enabled?: boolean;
  healthCategoryIds?: string[];
};

export const useGetIntakeStatusCustomSteps = ({
  patientId,
  enabled = true,
  healthCategoryIds,
}: UseGetIntakeStatusCustomStepsParams) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<IntakeStatusCustomSteps | null>(null);
  const [error, setError] = React.useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || healthCategoryIds?.length === 0) {
      setIsLoading(false);
      setData(null);
      setError(null);

      return;
    }

    const snapshotQuery = getQuery({ patientId, healthCategoryIds });

    const unsubscribe = onSnapshot(
      snapshotQuery,
      (snapshot) => {
        const intakeStatus = snapshot.docs.reduce<IntakeStatusCustomSteps>((acc, doc) => {
          const data = doc.data() as CustomStep[CustomStepType];

          const { healthCategoryId } = data;

          return {
            ...acc,
            [healthCategoryId]: {
              ...acc[healthCategoryId],
              [doc.id]: {
                ...data,
                id: doc.id,
              },
            } satisfies IntakeStatusCustomSteps[HealthCategoryId],
          } satisfies IntakeStatusCustomSteps;
        }, {} as IntakeStatusCustomSteps);

        setData(intakeStatus);
        setIsLoading(false);
        setError(null);
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('Error fetching patient intake status:', error);
        }

        captureException(error, {
          tags: { issueIn: 'useGetIntakeStatusCustomSteps' },
          extra: { patientId },
        });

        setError(error);
        setIsLoading(false);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [enabled, healthCategoryIds, patientId]);

  return { data, isLoading, error };
};
